import { css } from '@emotion/react';
import {
  ActionModal,
  Button,
  Col,
  colors,
  Row,
  CheckboxGroup,
  Text,
  useDisclosure,
} from 'common-components';
import { useAppTranslation } from '@hooks';
import { FiltersBarForm } from '@views/shared/form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getWirkUrl, FormState } from '@definitions';
import { getStateKeyByValue } from '@utils';
import { deleteForm, duplicateForm, GetFormsPayloadType } from '@api';
import { useState } from 'react';
import { Pagination, PaginationProps } from 'antd';
import openLink from '@utils/openLink';
import { useParams } from 'react-router';
import { FormType } from '@pages/types';

import ManagementCardForm from './ManagementCardForm';
import FiltersForm from './FiltersForm';

import { store } from '@/store';

type ListFormPropsType = {
  formDrafts: FormType[]
  forms: FormType[]
  reqParams: GetFormsPayloadType
  setParams: (filters: GetFormsPayloadType) => void
  pagination: PaginationProps
};
const ListForm = ({ formDrafts, forms, pagination, setParams, reqParams }: ListFormPropsType) => {
  const client = useQueryClient();
  const { translate } = useAppTranslation();
  const { open, isOpen, close } = useDisclosure();
  const accessToken = store.use.me().accessToken;
  const userId = store.use.me().userData.idUser;
  const params = useParams();

  const [idFormsToDelete, setIdFormsToDelete] = useState<string[]>([]);
  const [isFiltersVisible, setFiltersVisible] = useState(false);

  const deleteFormMutation = useMutation({
    mutationFn: deleteForm,
    onSuccess: () => {
      // setFormDrafts([])
      client.invalidateQueries();
    },
  });

  const onFormsDelete = async () => {
    for (const idForm of idFormsToDelete) {
      await deleteFormMutation.mutateAsync(idForm);
    }
  };
  const onFormDeleteConfirm = async () => {
    await deleteFormMutation.mutateAsync(idFormsToDelete[0]);
    setIdFormsToDelete([]);
    close();
  };

  const onClickFormCreate = () =>
    openLink(`${getWirkUrl('Form')}?token=${accessToken}&tenant=${params.tenant}`);

  const onFormOpen = (idForm: string) =>
    openLink(
      `${getWirkUrl('Form')}/form/${idForm}/fields?token=${accessToken}&tenant=${params.tenant}`,
    );
  const onFormCopy = async (idForm: string) => {
    try {
      const formCopy = await duplicateForm(idForm);
      await client.invalidateQueries();
      openLink(
        `${getWirkUrl('Form')}/form/${formCopy.idForm}/fields?token=${accessToken}&tenant=${
          params.tenant
        }`,
      );
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  const onFormDelete = (idForm: string) => {
    setIdFormsToDelete([idForm]);
    open();
  };

  return (
    <div css={$page}>
      <div css={$drafts}>
        <Text size="lg" color={colors.gray900} style={{ fontWeight: 500 }}>
          {translate('form.formDraftsListTitle')}
        </Text>
        <div css={$grid}>
          {formDrafts.map((form, i) => (
            <ManagementCardForm
              key={`${i}-${form.idForm}`}
              form={form}
              onFormOpen={onFormOpen}
              onFormCopy={onFormCopy}
              onFormDelete={onFormDelete}
            />
          ))}
        </div>
      </div>
      <div css={$column}>
        <Row align="middle" justify="space-between" style={{ width: '100%' }}>
          <Text size="lg" color={colors.gray900} style={{ fontWeight: 500 }}>
            {idFormsToDelete.length
              ? translate('form.selectedCount', { count: idFormsToDelete.length })
              : translate('form.formListTitle')}
          </Text>
          {idFormsToDelete.length ? (
            <Button iconLeft="Trash01" onClick={onFormsDelete}>
              {translate('certificationPage.archiveButtonLabel')}
            </Button>
          ) : (
            <Button onClick={onClickFormCreate} type="primary" iconLeft="Plus">
              {translate('form.addButton')}
            </Button>
          )}
        </Row>
        <Row
          justify={'space-between'}
          align={'middle'}
          style={{
            width: '100%',
          }}
        >
          <Col span={16}>
            <FiltersBarForm initValues={reqParams} onFiltersChange={setParams} />
          </Col>

          <Button onClick={() => setFiltersVisible(true)} iconLeft="FilterLines">
            {translate('common.filter')}
          </Button>

          <FiltersForm
            visible={isFiltersVisible}
            onClose={() => setFiltersVisible(false)}
            initValues={reqParams}
            onFiltersChange={setParams}
          />
        </Row>
        <CheckboxGroup
          onChange={(selected: string[]) => {
            setIdFormsToDelete(selected);
          }}
          value={idFormsToDelete}
          style={{ width: '100%' }}
        >
          <div css={$grid}>
            {forms.map((form, i) => (
              <ManagementCardForm
                key={`${i}-${form.idForm}`}
                form={form}
                onFormOpen={onFormOpen}
                onFormCopy={onFormCopy}
                onFormDelete={
                  userId === form.idUser && form.state !== getStateKeyByValue(FormState, 'archived')
                    ? onFormDelete
                    : undefined
                }
                canSelect={
                  userId === form.idUser && form.state !== getStateKeyByValue(FormState, 'archived')
                }
              />
            ))}
          </div>
        </CheckboxGroup>
      </div>
      <ActionModal
        open={isOpen}
        title={translate('common.deleting')}
        icon={'Trash02'}
        iconColor={colors.error600}
        iconBgColor={colors.error100}
        onConfirm={() => onFormDeleteConfirm()}
        onCancel={close}
        actionButtons={[
          {
            text: translate('common.buttons.return'),
            onClick: close,
          },
          {
            type: 'primary',
            text: translate('common.buttons.delete'),
            onClick: () => onFormDeleteConfirm(),
          },
        ]}
      >
        <Text>{translate('common.areYouSure')}</Text>
      </ActionModal>
      {/*FIXME: move pagination to common with predefined styles*/}
      <Row justify={'end'} style={{ width: '100%', padding: '10px 0' }}>
        <Pagination {...pagination} />
      </Row>
    </div>
  );
};

const $page = css`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: 24px;
`;

const $drafts = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 24px;
  border: 1px solid ${colors.gray200};
  border-radius: 24px;
  background: ${colors.gray25};
`;

const $column = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const $grid = css`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 16px 20px;
`;

export default ListForm;
